import { useDebounce } from 'features';
import { useFormContext, useWatch } from 'form';
import { useCallback, useEffect } from 'react';
import { Divider, Grid } from 'ui';

import { useMatchedSiteMaterialItem } from '../../contexts/MatchedSiteMaterialItemContext';
import { useTotalNetTakeRateCalculator } from '../../hooks/useTotalNetTakeRateCalculator/useTotalNetTakeRateCalculator';
import { useTransportCalculator } from '../../hooks/useTransportCalculator/useTransportCalculator';
import { DealCalculatorDrawerSection } from '../DealCalculatorDrawerSection/DealCalculatorDrawerSection';
import { MatchedDemandSignalOverview } from '../MatchedDemandSignalOverview/MatchedDemandSignalOverview';
import type { DrawerFormValues } from '../MatchedSiteMaterialItemDrawer/MatchedSiteMaterialItemDrawer';
import { MatchedSupplySignalOverview } from '../MatchedSupplySignalOverview/MatchedSupplySignalOverview';
import { RecentTradingActivitySection } from '../RecentTradingActivitySection/RecentTradingActivitySection';
import { SiteMaterialOverview } from '../SiteMaterialOverview/SiteMaterialOverview';

const DEBOUNCE_TIME = 300;

export const MatchedSiteMaterialItemContent = () => {
  const { calculateTotalNetTakeRate } = useTotalNetTakeRateCalculator();
  const { demandSignal, siteMaterial } = useMatchedSiteMaterialItem();
  const { setValue, getValues } = useFormContext<DrawerFormValues>();
  const { estimateTransportPrice, transportPriceError, isLoading: isTransportPriceLoading } = useTransportCalculator();

  const debouncedCalculateTotalNetTakeRate = useDebounce(calculateTotalNetTakeRate, DEBOUNCE_TIME);

  const onEstimateTransportPrice = useCallback(async () => {
    const data = getValues();
    const transportPrice = await estimateTransportPrice({
      currency: data.transport_currency,
      demandIncoterm: data.demand_incoterm,
      demandSignal,
      port: data.port_of_loading,
      siteMaterial,
      supplyIncoterm: data.supply_incoterm,
    });
    const transportCost =
      transportPrice?.adjusted_total_cost_in_target_currency !== undefined
        ? Math.ceil(Number(transportPrice.adjusted_total_cost_in_target_currency))
        : undefined;
    setValue('transport_cost', transportCost);
    setValue('estimated_transit_time_days', transportPrice.estimated_transit_time_days || undefined);
    calculateTotalNetTakeRate();
  }, [setValue, getValues, demandSignal, siteMaterial, estimateTransportPrice, calculateTotalNetTakeRate]);

  const onTransportPriceUpdate = async () => {
    calculateTotalNetTakeRate();
  };

  useEffect(() => {
    onEstimateTransportPrice();
  }, [onEstimateTransportPrice]);

  const buyerPrice = useWatch({ name: 'buyer_price_per_tonne' });
  const buyerCurrency = useWatch({ name: 'buyer_currency' });
  const sellerPrice = useWatch({ name: 'seller_price_per_tonne' });
  const sellerCurrency = useWatch({ name: 'seller_currency' });

  useEffect(() => {
    debouncedCalculateTotalNetTakeRate();
  }, [buyerPrice, buyerCurrency, sellerPrice, sellerCurrency, debouncedCalculateTotalNetTakeRate]);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={4}>
        <SiteMaterialOverview variant="muted" />
      </Grid>
      <Grid item xs={12} md={4}>
        <MatchedDemandSignalOverview variant="muted" showPrice={false} />
      </Grid>
      <Grid item xs={12} md={4}>
        <MatchedSupplySignalOverview variant="muted" showPrice={false} />
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <DealCalculatorDrawerSection
          estimateTransportPrice={onEstimateTransportPrice}
          onTransportPriceUpdate={onTransportPriceUpdate}
          isTransportPriceLoading={isTransportPriceLoading}
          transportPriceError={transportPriceError}
        />
      </Grid>

      <Grid item xs={12}>
        <RecentTradingActivitySection />
      </Grid>
    </Grid>
  );
};
